/**
 * Client-side helpers for help-chat visualisations.
 *
 * The server validates every payload strictly (functions-common
 * agents/helpChat/visualise/schemas.js). The guard here is defensive: the
 * conversation is user-writable, and the Realtime Database drops empty arrays,
 * so payloads are re-checked and empty lists restored before rendering.
 */

export const DISPLAYS = ["chart", "table", "kpi", "timeline", "flow", "report"];
export const SECTION_DISPLAYS = ["chart", "table", "kpi", "timeline", "flow"];
export const CHART_KINDS = ["line", "bar", "area", "pie"];
export const DATA_FENCE = "stc-visualisation";
export const SLOT_PREFIX = "stc-visualisation-image:";
export const SLOT_TOKEN = "STCVISUALISATIONSLOT";

const SLOT_COMMENT = new RegExp(`<!--\\s*${SLOT_PREFIX}(\\d+)\\s*-->`, "g");

function isObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function asList(value) {
  if (Array.isArray(value)) return value;
  if (isObject(value)) {
    const keys = Object.keys(value);
    if (keys.every((key) => /^\d+$/.test(key))) return keys.sort((a, b) => a - b).map((key) => value[key]);
  }
  return value == null ? [] : null;
}

export function isInternalRoute(value) {
  return typeof value === "string"
    && value.startsWith("/")
    && !value.startsWith("//")
    && !value.includes(":")
    && !/\s/.test(value);
}

function text(value) {
  return typeof value === "string" ? value : "";
}

function route(value) {
  return isInternalRoute(value) ? value : "";
}

function cleanPart(part, allowed) {
  if (!isObject(part) || !allowed.includes(part.display)) throw new Error("Unknown display type.");
  if (!text(part.title).trim()) throw new Error("The visualisation has no title.");
  const base = {
    display: part.display,
    title: part.title,
    description: text(part.description),
    total: Number.isFinite(part.total) ? part.total : 0,
  };

  if (part.display === "chart" || part.display === "table") {
    const columns = asList(part.columns);
    const rows = asList(part.rows);
    if (!columns?.length || !rows?.length) throw new Error("The data has no rows.");
    for (const column of columns) {
      if (!isObject(column) || typeof column.key !== "string" || !text(column.label)) throw new Error("A column is malformed.");
    }
    const cleanRows = rows.map((row) => {
      const cells = asList(row);
      if (!cells || cells.length !== columns.length) throw new Error("A row does not match the columns.");
      return cells.map((cell) => (typeof cell === "number" || typeof cell === "string" ? cell : ""));
    });
    const cleaned = { ...base, columns: columns.map((column) => ({ key: column.key, label: column.label, type: column.type || "string" })), rows: cleanRows };
    if (part.display === "chart") {
      const chart = isObject(part.chart) ? part.chart : {};
      const series = asList(chart.series) || [];
      const keys = new Set(columns.map((column) => column.key));
      if (!CHART_KINDS.includes(chart.kind) || !keys.has(chart.x) || !series.length || !series.every((key) => keys.has(key))) {
        throw new Error("The chart settings are malformed.");
      }
      cleaned.chart = { kind: chart.kind, x: chart.x, series };
      if (text(chart.yLabel).trim()) cleaned.chart.yLabel = chart.yLabel.trim().slice(0, 80);
    } else {
      const links = asList(part.links) || [];
      cleaned.links = cleanRows.map((row, index) => route(links[index]));
    }
    return cleaned;
  }
  if (part.display === "kpi") {
    const kpis = asList(part.kpis);
    if (!kpis?.length) throw new Error("There are no figures.");
    return {
      ...base,
      kpis: kpis.map((kpi) => {
        if (!isObject(kpi) || !Number.isFinite(kpi.value) || !text(kpi.label)) throw new Error("A figure is malformed.");
        return { label: kpi.label, value: kpi.value, hint: text(kpi.hint) };
      }),
    };
  }
  if (part.display === "timeline") {
    const events = asList(part.events);
    if (!events?.length) throw new Error("There are no events.");
    return {
      ...base,
      events: events.map((event) => {
        if (!isObject(event) || !/^\d{4}-\d{2}/.test(text(event.date))) throw new Error("An event is malformed.");
        return { date: event.date, label: text(event.label), title: text(event.title), type: text(event.type), route: route(event.route) };
      }),
    };
  }
  if (part.display === "flow") {
    const nodes = asList(part.nodes);
    if (!nodes?.length) throw new Error("There are no records to link.");
    const cleanNodes = nodes.map((node) => {
      if (!isObject(node) || !/^n\d{1,3}$/.test(text(node.id))) throw new Error("A record is malformed.");
      return { id: node.id, label: text(node.label) || "Record", title: text(node.title), type: text(node.type), route: route(node.route) };
    });
    const ids = new Set(cleanNodes.map((node) => node.id));
    const edges = (asList(part.edges) || []).filter((edge) => isObject(edge) && ids.has(edge.from) && ids.has(edge.to));
    return { ...base, nodes: cleanNodes, edges: edges.map((edge) => ({ from: edge.from, to: edge.to })) };
  }
  const sections = asList(part.sections);
  if (!sections?.length) throw new Error("The report has no sections.");
  return { ...base, sections: sections.map((section) => cleanPart(section, SECTION_DISPLAYS)) };
}

/**
 * @param {unknown} data
 * @returns {{ ok: true, data: object } | { ok: false, error: string }}
 */
export function normaliseVisualisation(data) {
  try {
    const cleaned = cleanPart(data, DISPLAYS);
    if (isObject(data) && typeof data.fileId === "string" && /^[A-Za-z0-9_-]{1,64}$/.test(data.fileId)) cleaned.fileId = data.fileId;
    return { ok: true, data: cleaned };
  } catch (error) {
    return { ok: false, error: error?.message || "The visualisation could not be read." };
  }
}

/** Top-level parts in document order: the sections of a report, or the payload itself. */
export function visualParts(data) {
  return data?.display === "report" ? data.sections : [data];
}

/** Chart and flow parts get a captured image. */
export function hasImage(part) {
  return part?.display === "chart" || part?.display === "flow";
}

export function snapshotName(position) {
  return position === 0 ? "chart.png" : `chart-${position}.png`;
}

export function rowObjects(part) {
  return part.rows.map((row) => Object.fromEntries(part.columns.map((column, index) => [column.key, row[index]])));
}

/** AG Charts (v7 community) options for a chart part. Values are shown with thousands separators. */
export function toAgOptions(part, locale) {
  const data = rowObjects(part);
  const labels = new Map(part.columns.map((column) => [column.key, column.label]));
  const xLabel = labels.get(part.chart.x);
  const tooltip = (key) => ({
    renderer: ({ datum, title }) => ({ title, content: `${datum?.[part.chart.x] ?? ""}: ${formatNumber(datum?.[key], locale)}` }),
  });
  if (part.chart.kind === "pie") {
    const key = part.chart.series[0];
    return {
      data,
      autoSize: true,
      series: [{
        type: "pie",
        angleKey: key,
        angleName: labels.get(key),
        calloutLabelKey: part.chart.x,
        sectorLabelKey: key,
        sectorLabel: { formatter: ({ datum }) => formatNumber(datum?.[key], locale) },
        innerRadiusRatio: 0.5,
        tooltip: tooltip(key),
      }],
      legend: { enabled: true, position: "bottom" },
    };
  }
  const type = part.chart.kind === "bar" ? "column" : part.chart.kind;
  const many = part.chart.series.length > 1;
  return {
    data,
    autoSize: true,
    series: part.chart.series.map((key) => ({
      type,
      xKey: part.chart.x,
      xName: xLabel,
      yKey: key,
      yName: labels.get(key),
      tooltip: tooltip(key),
      ...(type === "column" && many ? { grouped: true } : {}),
      ...(type === "line" ? { marker: { enabled: data.length <= 60 } } : {}),
    })),
    axes: [
      { type: "category", position: "bottom", title: { text: xLabel } },
      {
        type: "number",
        position: "left",
        title: { text: part.chart.yLabel || "Count" },
        label: { formatter: ({ value }) => formatNumber(value, locale) },
      },
    ],
    legend: { enabled: many, position: "bottom" },
    navigator: { enabled: data.length > 12, height: 24 },
  };
}

function mermaidLabel(value) {
  return String(value || "").replace(/["`<>#;{}[\]|]/g, "").replace(/\s+/g, " ").trim().slice(0, 60) || "Record";
}

/** Mermaid flowchart text for a flow part. Labels are stripped of Mermaid syntax. */
export function toMermaid(part) {
  const lines = ["flowchart LR"];
  for (const node of part.nodes) lines.push(`  ${node.id}["${mermaidLabel(node.label)}<br/>${mermaidLabel(node.type)}"]`);
  for (const edge of part.edges) lines.push(`  ${edge.from} --- ${edge.to}`);
  return lines.join("\n");
}

/**
 * Pull the `stc-visualisation` payload out of a generated file and drop its
 * "Visualisation data" section from the Markdown that will be shown.
 */
export function extractVisualisation(markdown) {
  const source = String(markdown || "");
  const fence = new RegExp(`(?:^|\\n)(?:## Visualisation data\\s*\\n+)?\`\`\`${DATA_FENCE}\\n([\\s\\S]*?)\\n\`\`\`[ \\t]*(?=\\n|$)`);
  const match = fence.exec(source);
  if (!match) return { data: null, markdown: source };
  let data = null;
  try {
    const parsed = normaliseVisualisation(JSON.parse(match[1]));
    data = parsed.ok ? parsed.data : null;
  } catch {
    data = null;
  }
  return { data, markdown: `${source.slice(0, match.index)}${source.slice(match.index + match[0].length)}`.trimEnd() };
}

/** Remove fenced mermaid blocks (the interactive or captured diagram replaces them). */
export function stripMermaid(markdown) {
  return String(markdown || "").replace(/```mermaid\n[\s\S]*?\n```[ \t]*\n?/g, "");
}

/**
 * Replace each slot comment with a paragraph token that survives Markdown
 * rendering. The captured image on the line after a slot is dropped, because
 * the interactive part takes its place.
 */
export function markSlots(markdown) {
  return String(markdown || "")
    .replace(new RegExp(`<!--\\s*${SLOT_PREFIX}(\\d+)\\s*-->\\n!\\[[^\\]\\n]*\\]\\(chart(?:-\\d+)?\\.png\\)`, "g"), (_, position) => `<!-- ${SLOT_PREFIX}${position} -->`)
    .replace(SLOT_COMMENT, (_, position) => `\n\n${SLOT_TOKEN}${position}\n\n`);
}

/** Split rendered HTML on slot tokens: `[{ html }, { slot: 0 }, { html }]`. */
export function splitSlots(html) {
  const pieces = [];
  const pattern = new RegExp(`<p>${SLOT_TOKEN}(\\d+)</p>`, "g");
  let cursor = 0;
  let match;
  while ((match = pattern.exec(html)) !== null) {
    if (match.index > cursor) pieces.push({ html: html.slice(cursor, match.index) });
    pieces.push({ slot: Number(match[1]) });
    cursor = match.index + match[0].length;
  }
  if (cursor < html.length) pieces.push({ html: html.slice(cursor) });
  return pieces;
}

/**
 * Prepare a generated file for PDF: each slot becomes its captured image, a
 * freshly drawn image from `drawPart`, or nothing (tables already follow).
 */
export async function pdfMarkdown(markdown, { drawPart } = {}) {
  const { data, markdown: body } = extractVisualisation(markdown);
  const parts = data ? visualParts(data) : [];
  const withoutDiagrams = data ? stripMermaid(body) : body;
  const slots = [...withoutDiagrams.matchAll(new RegExp(`<!--\\s*${SLOT_PREFIX}(\\d+)\\s*-->(\\n!\\[[^\\]\\n]*\\]\\([^)\\n]+\\))?`, "g"))];
  let result = "";
  let cursor = 0;
  for (const match of slots) {
    result += withoutDiagrams.slice(cursor, match.index);
    cursor = match.index + match[0].length;
    const position = Number(match[1]);
    if (match[2]) {
      result += match[2].replace(/^\n/, "");
      continue;
    }
    const part = parts[position];
    if (hasImage(part) && typeof drawPart === "function") {
      try {
        const image = await drawPart(part);
        if (image) result += `![${mermaidLabel(part.title)}](${image})`;
      } catch (error) {
        console.warn("[HelpChat] Could not draw a chart for the PDF", error);
      }
    }
  }
  return result + withoutDiagrams.slice(cursor);
}

export function formatNumber(value, locale) {
  return Number.isFinite(value) ? new Intl.NumberFormat(locale || undefined).format(value) : "";
}
